import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { Analytics } from "@vercel/analytics/react"
import { type FC, type ReactNode, useEffect, useLayoutEffect, useRef, useState } from "react"
import {
	type ClientOnErrorFunction,
	createBrowserRouter,
	Navigate,
	Outlet,
	RouterProvider,
	useLocation,
	useParams,
	useSearchParams,
} from "react-router"
import { Tooltip } from "react-tooltip"
import { getCookie, setCookie } from "react-use-cookie"
import { ThemeProvider } from "styled-components"

import { parseIntNumber } from "@forento/core/utilities/number"
import "@forento/ui/assets/landing-fonts.css"
import "@forento/ui/assets/lexical.css"
import "@forento/ui/assets/rich-content.css"
import useActivityTracking from "@forento/ui/hooks/useActivityTracking"
import { combineContextProviders } from "@forento/ui/utilities/component"

import appRoutes from "~/appRoutes"
import AdminPanel from "~/components/AdminPanel"
import ClientOutdatedToast from "~/components/ClientOutdatedToast"
import ConnectionBanner from "~/components/ConnectionBanner"
import CookieConsent from "~/components/CookieConsent"
import ErrorBoundary, { RouteErrorBoundary, reportError } from "~/components/ErrorBoundary"
import FullLoadingPage from "~/components/FullLoadingPage"
import { AppLayout } from "~/components/Layout"
import LoadingError from "~/components/LoadingError"
import AlertContextProvider from "~/contexts/AlertContext/Provider"
import { useBrand } from "~/contexts/BrandContext"
import BrandProvider from "~/contexts/BrandContext/Provider"
import GoogleSignInContextProvider from "~/contexts/GoogleSignInContext/Provider"
import ToastContextProvider from "~/contexts/ToastContext/Provider"
import { useUser } from "~/contexts/UserContext"
import UserContextProvider from "~/contexts/UserContext/Provider"
import VersionContextProvider from "~/contexts/VersionContext/Provider"
import VideoUploadProvider from "~/contexts/VideoUploadContext/Provider"
import routes from "~/utilities/routes"
import trpc, { keepaliveTrpc, query } from "~/utilities/trpc"

import "~/assets/styles.css"

export type Authorization = "none" | "require" | "forbid"

const AppThemeProvider: FC<{ children: ReactNode }> = ({ children }) => (
	<ThemeProvider theme={{ primaryColor: useBrand().primaryColor }}>{children}</ThemeProvider>
)

const AppContextProviders = combineContextProviders([
	VersionContextProvider,
	BrandProvider,
	AppThemeProvider,
	UserContextProvider,
	AlertContextProvider,
	ToastContextProvider,
	GoogleSignInContextProvider,
	VideoUploadProvider,
])

const RouterWrapper: FC = () => {
	const [queryClient] = useState(
		() =>
			new QueryClient({
				defaultOptions: { queries: { staleTime: 30_000 } },
			}),
	)

	return (
		<query.Provider client={trpc} queryClient={queryClient}>
			<QueryClientProvider client={queryClient}>
				<AppContextProviders>
					<Analytics />
					<ClientOutdatedToast />
					<ConnectionBanner />
					<ImpersonationGrantWrapper>
						<Outlet />
						<Tooltip id="tooltip" />
						<CookieConsent />
						{CONFIG.isDevelopment && <AdminPanel />}
					</ImpersonationGrantWrapper>
				</AppContextProviders>
			</QueryClientProvider>
		</query.Provider>
	)
}

const AuthorizedRoute: FC<{ authorization: Authorization }> = ({ authorization }) => (
	<Content authorization={authorization}>
		<Outlet />
	</Content>
)

const ImpersonationGrantWrapper: FC<{ children: ReactNode }> = ({ children }) => {
	const [searchParams, setSearchParams] = useSearchParams()
	const { reload } = useUser()
	const claimedTokenRef = useRef<string | null>(null)

	const token = searchParams.get("impersonation-grant")

	useEffect(() => {
		if (token === null || claimedTokenRef.current === token) return
		claimedTokenRef.current = token

		let isActive = true

		const claim = async () => {
			try {
				const result = await trpc.user.claimImpersonationGrant.mutate({ token })
				if (result.status === "success") {
					await reload({ clearSessionCache: true })
				}
			} catch (error) {
				console.error(error)
				if (isActive) {
					setSearchParams(searchParams => {
						const nextSearchParams = new URLSearchParams(searchParams)
						nextSearchParams.delete("impersonation-grant")
						return nextSearchParams
					})
				}
				return Promise.reject(error)
			}
			if (isActive) {
				setSearchParams(searchParams => {
					const nextSearchParams = new URLSearchParams(searchParams)
					nextSearchParams.delete("impersonation-grant")
					return nextSearchParams
				})
			}
		}

		claim().catch(() => {})

		return () => {
			isActive = false
		}
	}, [token, setSearchParams, reload])

	if (token !== null) return <FullLoadingPage text="Signing you in..." />

	return children
}

const Content: FC<{ authorization: Authorization; children: ReactNode }> = ({ authorization, children }) => {
	const location = useLocation()
	const brand = useBrand()
	const user = useUser()
	const utilities = query.useUtils()
	const [searchParams] = useSearchParams()

	useLayoutEffect(() => {
		document.title = brand.name
		document.documentElement.style.setProperty(`--color-primary`, brand.primaryColor)
	}, [brand.name, brand.primaryColor])

	useEffect(() => {
		window.scrollTo({ left: 0, top: 0, behavior: "auto" })
	}, [location.pathname])

	useActivityTracking({
		userId: user.user && !user.user.isImpersonating ? user.user.id : null,
		createSession: () => utilities.client.activity.createSession.mutate(),
		updateSession: input => utilities.client.activity.updateSession.mutate(input),
		flushSession: input => {
			keepaliveTrpc.activity.updateSession.mutate(input).catch(() => {})
		},
	})

	useEffect(() => {
		const sourceParam = searchParams.get("s")
		const sourceCookie = getCookie("s")
		if (sourceParam === null || sourceCookie.length > 0) return

		setCookie("s", sourceParam, {
			days: 90,
			path: "/",
			domain: CONFIG.isDevelopment ? "localhost" : "forento.io",
		})
	}, [searchParams])

	useEffect(() => {
		if (CONFIG.isDevelopment) return

		if (user.user?.intercomToken) {
			window.Intercom("boot", {
				api_base: "https://api-iam.intercom.io",
				app_id: "xu1aznd7",
				intercom_user_jwt: user.user.intercomToken,
			})
		} else {
			window.Intercom("shutdown")
		}
	}, [user.user])

	const isPosthogIdentifiedRef = useRef(false)
	useEffect(() => {
		if (user.isLoading) return

		if (user.user?.isImpersonating) {
			window.posthog?.opt_out_capturing()
			window.posthog?.reset()
			isPosthogIdentifiedRef.current = false
		} else if (user.user === undefined) {
			// Reset only when a session ends; resetting every signed-out visit would turn each into a new person
			if (isPosthogIdentifiedRef.current) window.posthog?.reset()
			isPosthogIdentifiedRef.current = false
			window.posthog?.opt_in_capturing()
		} else {
			isPosthogIdentifiedRef.current = true
			window.posthog?.identify(user.user.id.toString(), {
				email: user.user.email,
				name: `${user.user.firstName} ${user.user.lastName}`,
				created_at: user.user.registerDate.toISOString(),
			})
			window.posthog?.opt_in_capturing()
		}
	}, [user.isLoading, user.user])

	if (user.error && !user.user)
		return <LoadingError fullPage subject="your account" error={user.error} onRetry={() => user.reload()} />

	if (user.isLoading) return <FullLoadingPage text="Loading your account..." />

	if (user.user && !user.user.platform && location.pathname !== routes.account.createPlatform()) {
		return <Navigate to={routes.account.createPlatform()} replace />
	}

	if (
		user.user &&
		user.user.platform &&
		!user.user.platform.contactEmail &&
		location.pathname !== routes.account.setContactEmail()
	) {
		return <Navigate to={routes.account.setContactEmail()} replace />
	}

	if (
		user.user &&
		user.user.platform &&
		user.user.platform.contactEmail &&
		!user.user.platform.hasChosenFindSource &&
		location.pathname !== routes.account.selectFindSource()
	) {
		return (
			<Navigate
				to={`${routes.account.selectFindSource()}?next=${encodeURIComponent(location.pathname + location.search)}`}
				replace
			/>
		)
	}

	if (
		user.user &&
		user.user.platform &&
		user.user.platform.contactEmail &&
		user.user.platform.hasChosenFindSource &&
		!user.user.platform.hasChosenIntent &&
		location.pathname !== routes.account.selectIntent()
	) {
		return (
			<Navigate
				to={`${routes.account.selectIntent()}?next=${encodeURIComponent(location.pathname + location.search)}`}
				replace
			/>
		)
	}

	if (user.user === undefined && authorization === "require")
		return (
			<Navigate
				to={`${routes.account.signin()}?next=${encodeURIComponent(location.pathname + location.search)}`}
				replace
			/>
		)

	if (user.user !== undefined && authorization === "forbid") return <Navigate to={routes.main.index()} replace />

	return children
}

const EditProductAccessRedirect: FC = () => {
	const { emailId } = useParams()
	return <Navigate to={routes.settings.studentEmail.editProductAccess(parseIntNumber(emailId) ?? -1)} replace />
}

const EditStudentEmailRedirect: FC = () => {
	const { emailId } = useParams()
	return <Navigate to={routes.settings.studentEmail.detail(emailId ?? "unknown")} replace />
}

const router = createBrowserRouter([
	{
		element: <RouterWrapper />,
		ErrorBoundary: RouteErrorBoundary,
		children: [
			...(["none", "require", "forbid"] as const).map(authorization => ({
				element: <AuthorizedRoute authorization={authorization} />,
				children: [
					{
						element: <AppLayout />,
						children: appRoutes
							.filter(route => route.auth === authorization && route.layout === "app")
							.map(({ path, component: Component }) => ({ path, element: <Component /> })),
					},
					...appRoutes
						.filter(route => route.auth === authorization && route.layout !== "app")
						.map(({ path, component: Component }) => ({ path, element: <Component /> })),
				],
			})),
			{
				path: "/settings/platform",
				element: <Navigate to={routes.settings.customizeAcademy()} replace />,
				ErrorBoundary: RouteErrorBoundary,
			},
			{
				path: "/settings/student-emails",
				element: <Navigate to={routes.settings.studentEmail.index()} replace />,
				ErrorBoundary: RouteErrorBoundary,
			},
			{
				path: "/settings/student-emails/layout",
				element: <Navigate to={routes.settings.studentEmail.index()} replace />,
				ErrorBoundary: RouteErrorBoundary,
			},
			{
				path: "/settings/email-automation/layout",
				element: <Navigate to={routes.settings.studentEmail.index()} replace />,
				ErrorBoundary: RouteErrorBoundary,
			},
			{
				path: "/settings/student-emails/product-access",
				element: <Navigate to={routes.settings.studentEmail.productAccess()} replace />,
				ErrorBoundary: RouteErrorBoundary,
			},
			{
				path: "/settings/student-emails/product-access/:emailId",
				element: <EditProductAccessRedirect />,
				ErrorBoundary: RouteErrorBoundary,
			},
			{
				path: "/settings/student-emails/:emailId",
				element: <EditStudentEmailRedirect />,
				ErrorBoundary: RouteErrorBoundary,
			},
		],
	},
])

const handleRouterError: ClientOnErrorFunction = (error, info) => {
	reportError(error, {
		location: `${info.location.pathname}${info.location.search}${info.location.hash}`,
		params: info.params,
		pattern: info.pattern,
		componentStack: info.errorInfo?.componentStack,
	})
}

const App: FC = () => (
	<ErrorBoundary>
		<RouterProvider router={router} onError={handleRouterError} />
	</ErrorBoundary>
)

export default App
